import { Delete, Restore } from '@mui/icons-material'
import CampaignIcon from '@mui/icons-material/Campaign'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContentText,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material'
import { AxiosError } from 'axios'
import { useFormik } from 'formik'
import { nanoid } from 'nanoid'
import { Suspense, useCallback, useEffect, useState } from 'react'
import {
  Outlet,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { toast } from 'react-toastify'

import ConfirmationDialog from '~/components/confirmationDialog'
import EditableTitle from '~/components/editableTitle'
import LoadingPaperSkeleton from '~/components/loadingPaperSkeleton'
import NotFound from '~/components/NotFound'
import useArchiveCampaigns from '~/features/campaigns/hooks/useArchiveCampaigns'
import useCreateCampaign from '~/features/campaigns/hooks/useCreateCampaign'
import useGetCampaignById from '~/features/campaigns/hooks/useGetCampaignById'
import useUpdateCampaign from '~/features/campaigns/hooks/useUpdateCampaign'
import {
  ECampaignChannel,
  ECampaignExecutionStatus,
  ECampaignKeys,
  ECampaignStatus,
  ECampaignTabs,
  ECampaignType,
  TCampaign,
  TCampaignCreatePayload,
  TCampaignError,
  TCampaignFact,
  TCampaignFormFields,
  TCampaignUpdatePayload,
} from '~/features/campaigns/models'
import {
  campaignValidationSchema,
  extractFriendlyErrorMessage,
  getArrayFromStringList,
  getMergeFields,
  getOutreachInstructionsDisplayText,
  getOutreachInstructionsSaveFields,
  getStringListFromArray,
  getTabFromRoute,
  showToastOnCampaignErrorResponse,
} from '~/features/campaigns/utils'
import {
  CREATE_CAMPAIGN_DEFAULT_ID,
  DEFAULT_CAMPAIGN_NAME,
  DEFAULT_CAMPAIGN_QUESTION,
} from '~/features/campaigns/utils/constants'
import { TKnowledgeTableDataItem } from '~/features/knowledge/v1/models'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { getChangedValues } from '~/features/settings/utils'
import { EClientPath } from '~/models/api'
import { sessionStore } from '~/store'
import pathBuilder from '~/utils/api'
import rem from '~/utils/ui'

function CreateEditCampaignContainer() {
  const theme = useTheme()
  const location = useLocation()
  const { id } = useParams()
  const [urlSearchParams] = useSearchParams()
  const isCopy = urlSearchParams.get('isCopy') === 'true'
  const navigate = useNavigate()
  const [tab, setTab] = useState(
    getTabFromRoute(location.pathname, id || CREATE_CAMPAIGN_DEFAULT_ID),
  )
  /*
  SaveAndTestDialog is a child component of CreateEditCampaignContainer
  I moved the open / close state here because of the current logic in place:
  * navigating once the campaign is created
  * rendering different components ( Loading ) if the campaign is creating, hence modifying the tree structure 
  */
  const [showSaveAndTestDialog, setShowSaveAndTestDialog] =
    useState<boolean>(false)
  const [isArchiveCampaignDialogOpen, setIsArchiveCampaignDialogOpen] =
    useState(false)
  const agentId = sessionStore((state) => state.agent.id) || ''
  const { partner } = usePartnerCentralized()
  const supportEmail = partner?.supportEmail

  const {
    data: { data } = {},
    isLoading,
    isError,
    error,
  } = useGetCampaignById((id !== CREATE_CAMPAIGN_DEFAULT_ID ? id : '') || '')

  const { mutate: createCampaign, isPending: isCreating } = useCreateCampaign()
  const { mutate: updateCampaign } = useUpdateCampaign(data?.id || '')

  const isArchived = data?.status === ECampaignStatus.archived && !isCopy
  const isLegacySimpleCampaign = data?.campaignType === ECampaignType.simple

  const { mutate: archiveCampaign, isPending: isDeleting } =
    useArchiveCampaigns(agentId, isArchived && !isLegacySimpleCampaign)

  const handleSubmitCreate = (
    values: TCampaignFormFields,
    questions: string[] | undefined,
    campaignFacts: TCampaignFact[] | undefined,
    outreachInstructionsText: string | undefined,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { id, ...valuesWithoutId } = values

    const createCampaignValues: TCampaignCreatePayload = {
      [ECampaignKeys.ofuChannels]: [ECampaignChannel.phone], // defaulting to phone -> HEY-3251, destructuring below will overwrite this in the future
      ...valuesWithoutId,
      questions,
      campaignFacts,
      ...getOutreachInstructionsSaveFields(outreachInstructionsText),
      agentId,
    }

    createCampaign(createCampaignValues, {
      onSuccess: (response) => {
        if ((response as TCampaignError).error) {
          toast.error(
            `Failed to create campaign. ${extractFriendlyErrorMessage(
              response as TCampaignError,
            )}`,
          )
          return
        }

        toast.success('Campaign created successfully')
        navigate({
          pathname: pathBuilder(EClientPath.campaignCreateEdit, {
            id: (response as TCampaign)?.id,
          }),
        })
      },
      onError: (response) =>
        showToastOnCampaignErrorResponse(response, 'Failed to create campaign'),
    })
  }

  const handleSubmitUpdate = (
    values: TCampaignFormFields,
    questions: string[] | undefined,
    campaignFacts: TCampaignFact[] | undefined,
    outreachInstructionsText: string | undefined,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const payload = getChangedValues(values, formik.initialValues)

    const updateCampaignValues = {
      ...payload,
      questions,
      campaignFacts,
      ...getOutreachInstructionsSaveFields(outreachInstructionsText),
    } as TCampaignUpdatePayload

    updateCampaign(updateCampaignValues, {
      onSuccess: () => {
        toast.success('Campaign updated successfully')
      },
      onError: (response) =>
        showToastOnCampaignErrorResponse(response, 'Failed to update campaign'),
    })
  }

  const formik = useFormik<TCampaignFormFields>({
    initialValues: {
      [ECampaignKeys.id]: isCopy ? '' : data?.id || '',
      [ECampaignKeys.campaignType]: data?.campaignType || ECampaignType.simple,
      [ECampaignKeys.campaignFacts]: !data?.campaignFacts
        ? []
        : data?.campaignFacts.map((fact) => {
            return {
              id: fact.id || nanoid(),
              fact: fact.text,
            } as TKnowledgeTableDataItem
          }),
      [ECampaignKeys.campaignName]: data?.campaignName || DEFAULT_CAMPAIGN_NAME,
      [ECampaignKeys.campaignObjective]: data?.campaignObjective || '',
      [ECampaignKeys.channel]: data?.channel || ECampaignChannel.phone,
      [ECampaignKeys.introMessage]: data?.introMessage || '',
      [ECampaignKeys.mergeFields]: getMergeFields(
        data?.mergeFields || [],
        data?.channel || ECampaignChannel.phone,
      ),
      [ECampaignKeys.questions]: getStringListFromArray(
        data?.questions || [DEFAULT_CAMPAIGN_QUESTION],
      ),
      [ECampaignKeys.outreachInstructionsText]:
        getOutreachInstructionsDisplayText(data ?? {}),
      [ECampaignKeys.status]: isCopy
        ? ECampaignStatus.draft
        : data?.status || ECampaignStatus.draft,
      [ECampaignKeys.useAgentFacts]: data?.useAgentFacts ?? true,
      [ECampaignKeys.voicemailMessage]: data?.voicemailMessage || '',
      [ECampaignKeys.executionStatus]:
        data?.executionStatus || ECampaignExecutionStatus.notStarted,
      [ECampaignKeys.startDate]: data?.startDate || undefined,
    },
    enableReinitialize: true,
    validationSchema: campaignValidationSchema,
    onSubmit: (values) => {
      if (!formik.dirty && !isCopy) {
        return
      }

      const questions = getArrayFromStringList(values[ECampaignKeys.questions])
      const campaignFacts = values[ECampaignKeys.campaignFacts]?.map((fact) => {
        return {
          id: fact.id,
          text: fact?.fact,
        } as TCampaignFact
      })
      const outreachInstructionsText =
        values[ECampaignKeys.outreachInstructionsText] ?? ''

      if (isCopy || id === CREATE_CAMPAIGN_DEFAULT_ID) {
        handleSubmitCreate(
          values,
          questions,
          campaignFacts,
          outreachInstructionsText,
        )
      } else {
        handleSubmitUpdate(
          values,
          questions,
          campaignFacts,
          outreachInstructionsText,
        )
      }
    },
  })

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent | null, newTab: ECampaignTabs) => {
      let pathByTab

      switch (newTab) {
        case ECampaignTabs.contacts:
          pathByTab = pathBuilder(EClientPath.campaignContacts, {
            id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
          })
          break
        case ECampaignTabs.results:
          pathByTab = pathBuilder(EClientPath.campaignResults, {
            id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
          })
          break
        case ECampaignTabs.details:
        default:
          pathByTab = pathBuilder(EClientPath.campaignDetails, {
            id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
          })
          break
      }

      setTab(newTab)
      navigate(pathByTab)
    },
    [id, navigate, isCopy],
  )

  useEffect(() => {
    if (
      location.pathname ===
      pathBuilder(EClientPath.campaignCreateEdit, {
        id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
      })
    ) {
      handleTabChange(null, ECampaignTabs.details)
    }
  }, [handleTabChange, id, location.pathname, navigate, isCopy])

  const shouldDisplayNotFound =
    isError &&
    (error as AxiosError)?.response?.status === 404 &&
    (error as AxiosError<{ message: string }>)?.response?.data?.message ===
      'Campaign not found'

  if (isError && !shouldDisplayNotFound) {
    toast.error('Error loading campaign')
    navigate(EClientPath.campaignList)

    return null
  }

  const handleArchiveCampaign = () => {
    archiveCampaign([{ id: data?.id || '', agentId }], {
      onSuccess: () => {
        toast.success(
          `Campaign ${isArchived ? 'restored' : 'archived'} successfully`,
        )
        setIsArchiveCampaignDialogOpen(false)
      },
      onError: () => {
        toast.error(
          `Failed to ${isArchived ? 'restore' : 'archive'} campaign. Please try again.`,
        )
      },
    })
  }

  const handleArchiveCampaignClick = () => {
    setIsArchiveCampaignDialogOpen(true)
  }

  const handleCancelArchiveClick = () => {
    setIsArchiveCampaignDialogOpen(false)
  }

  return (
    <>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Link
          to={EClientPath.campaignList}
          component={RouterLink}
          sx={{
            width: 'fit-content',
          }}
        >
          View all campaigns
        </Link>
        {isLegacySimpleCampaign && (
          <Alert
            severity="info"
            sx={{
              '&.MuiPaper-rounded': {
                boxShadow: 'none',
              },
            }}
          >
            <AlertTitle
              sx={{
                fontSize: rem(15),
              }}
            >
              You are viewing an older one-time campaign
            </AlertTitle>
            <Stack sx={{ fontSize: rem(15), gap: 1 }}>
              <Typography variant="body2" component="div">
                We have upgraded these legacy one-time campaigns with a richer
                setup and management experience. You can create these newer
                campaigns in the same campaign list you used to open this one
                under Campaign Manager.
              </Typography>
              <Typography variant="body2" component="div">
                To run this campaign going forward, create a new one-time
                campaign from Campaign Manager. It includes the latest options
                and improvements.
              </Typography>
              <Typography variant="body2" component="div">
                If you have questions, reach out to us at{' '}
                {supportEmail ? (
                  <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>
                ) : (
                  'our support team'
                )}
                .
              </Typography>
            </Stack>
          </Alert>
        )}
      </Stack>
      {isError && shouldDisplayNotFound && (
        <NotFound message="No campaign with that Id exists on this assistant." />
      )}
      {!isError && (
        <form onSubmit={formik.handleSubmit}>
          <Stack
            spacing={2}
            direction="row"
            sx={{ alignItems: 'center' }}
            marginBottom={2}
          >
            <CampaignIcon fontSize="large" />
            {isLoading ? (
              <LoadingPaperSkeleton sx={{ height: '2.9rem' }} />
            ) : (
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ width: '100%' }}
                alignItems="center"
              >
                <Stack direction="row" spacing={4} alignItems="center">
                  <EditableTitle
                    name={ECampaignKeys.campaignName}
                    ariaLabel="Campaign name"
                    value={formik.values.campaignName}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.campaignName &&
                      Boolean(formik.errors.campaignName)
                    }
                    helperText={
                      formik.touched.campaignName
                        ? formik.errors.campaignName
                        : ''
                    }
                    onSubmit={() =>
                      !isCopy &&
                      id !== CREATE_CAMPAIGN_DEFAULT_ID &&
                      updateCampaign({
                        campaignName: formik.values.campaignName,
                      })
                    }
                  />
                  {formik.values.status === ECampaignStatus.archived && (
                    <Chip label="Archived" color="error" />
                  )}
                </Stack>
                {!isCopy && !(isLegacySimpleCampaign && isArchived) && (
                  <Button
                    onClick={handleArchiveCampaignClick}
                    disabled={isDeleting}
                    variant="secondary"
                    size="small"
                  >
                    {!isDeleting && (
                      <Stack>
                        {isArchived ? (
                          <Restore sx={{ fontSize: rem(24), mr: 2 }} />
                        ) : (
                          <Delete sx={{ fontSize: rem(24), mr: 2 }} />
                        )}
                      </Stack>
                    )}
                    {isDeleting && (
                      <CircularProgress
                        size={rem(24)}
                        color="inherit"
                        sx={{ mr: 2 }}
                      />
                    )}
                    {isArchived ? 'Restore' : 'Archive'}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
          <Box sx={{ width: '100%' }}>
            <Box>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                aria-label="campaign tabs"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: rem(18),
                    color: theme.palette.secondaryButtonFont,
                    width: { xxs: rem(114), xs: rem(114), md: rem(170) },
                    height: rem(60),
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'transparent',
                  },

                  '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
                    color: theme.palette.secondaryButtonFont,
                    background: theme.palette.common.white,
                  },
                }}
              >
                <Tab label="Configure" />
                <Tab label="Contacts" disabled={!data?.id} />
                <Tab label="Results" disabled={!data?.id} />
              </Tabs>
            </Box>
            <Paper>
              {isLoading || isCreating || !formik ? (
                <LoadingPaperSkeleton sx={{ height: '70vh' }} />
              ) : (
                <Suspense fallback={<LoadingPaperSkeleton />}>
                  {formik && (
                    <Outlet
                      context={{
                        formik,
                        showSaveAndTestDialog,
                        setShowSaveAndTestDialog,
                      }}
                    />
                  )}
                </Suspense>
              )}
            </Paper>
          </Box>
        </form>
      )}

      <ConfirmationDialog
        title={isArchived ? `Restore Campaign` : `Archive Campaign`}
        open={isArchiveCampaignDialogOpen}
        handleCancel={handleCancelArchiveClick}
        handleConfirm={handleArchiveCampaign}
        isWarning
        isDisabled={isDeleting}
      >
        <DialogContentText>
          Are you sure you want to {isArchived ? 'restore ' : 'archive '}
          {formik.values.campaignName}?
        </DialogContentText>
      </ConfirmationDialog>
    </>
  )
}
export default CreateEditCampaignContainer
